import { EndpointAttributes, ApiAttribute } from '@context/openapi/types';

/**
 * Checks if any form field has unsaved data
 * Recursively checks params, queryParams, and pathParameters at all nesting levels
 */
export const hasUnsavedFormData = (
  attributes?: EndpointAttributes
): boolean => {
  if (!attributes) return false;

  const checkParams = (params?: ApiAttribute[]): boolean => {
    if (!params || params.length === 0) return false;

    return params.some((param) => {
      // Check if the current param has a value
      const hasValue =
        param.value !== undefined && param.value !== '' && param.value !== null;

      if (hasValue) return true;

      // Recursively check children at all depths
      return checkParams(param.child);
    });
  };

  return !!(
    checkParams(attributes.params) ||
    checkParams(attributes.queryParams) ||
    checkParams(attributes.pathParameters)
  );
};

/**
 * Checks if there's unsaved AI chat data
 * @param userPrompt - The current text in the AI prompt textarea
 * @param messagesCount - Number of messages in the conversation
 * @returns true if there's unsaved AI data
 */
export const hasUnsavedAIData = (
  userPrompt?: string,
  messagesCount?: number
): boolean => {
  // User has typed something in the textarea
  const hasPrompt = (userPrompt?.trim().length || 0) > 0;
  // User has an active conversation
  const hasConversation = (messagesCount || 0) > 0;

  return hasPrompt || hasConversation;
};

/**
 * Combined check for any unsaved data (form + AI chat)
 * Use this when you need to check both scenarios
 */
export const hasAnyUnsavedData = (
  attributes?: EndpointAttributes,
  userPrompt?: string,
  messagesCount?: number
): boolean => {
  return (
    hasUnsavedFormData(attributes) ||
    hasUnsavedAIData(userPrompt, messagesCount)
  );
};
